'use client'

import { SolidApple } from '@qr-front/icons/solid-apple'
import { cn } from '@qr-front/ui/utils'
import Script from 'next/script'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'

import { API_ROUTES } from '@/constants'
import { env } from '@/utils'

import { socialButtonStyle } from './shared'

export enum APPLE_AUTH_TYPE {
	Signup,
	Login,
}

const authTypeToAPIURL: Record<APPLE_AUTH_TYPE, string> = {
	[APPLE_AUTH_TYPE.Signup]: `${env.API_URL}${API_ROUTES.auth.appleSignUp}`,
	[APPLE_AUTH_TYPE.Login]: `${env.API_URL}${API_ROUTES.auth.appleLogin}`,
}

const CANCELLED_AUTH_ERRORS = [
	'popup_closed_by_user',
	'user_cancelled_authorize',
]

const APPLE_AUTH_SCRIPT =
	'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

export const AppleAuthButton = ({
	authType,
	variant = 'icon',
	className,
	labelClassName,
	onSubmit,
	onError,
}: {
	authType: APPLE_AUTH_TYPE
	variant?: 'icon' | 'text'
	className?: string
	labelClassName?: string
	onSubmit: (token: string, signup: boolean) => void
	onError?: (error: string) => void
}) => {
	const t = useTranslations()
	const clientId = env.APPLE_OAUTH_CLIENT_ID || ''
	const redirectURI = authTypeToAPIURL[authType]
	const handleClick = async () => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		window?.AppleID?.auth?.init({
			clientId,
			scope: 'email',
			redirectURI,
			responseMode: 'form_post',
			usePopup: env.ENV !== 'development',
		})
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		window?.AppleID?.auth?.signIn()
	}

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const handleAuthSuccess = (event: any) => {
			const code = event?.detail?.authorization?.code || ''
			if (code) {
				onSubmit(code, authType === APPLE_AUTH_TYPE.Signup)
			}
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const handleAuthFailure = (event: any) => {
			const error = event?.detail?.error || 'unknown_error'
			if (CANCELLED_AUTH_ERRORS.includes(error)) return
			onError?.(error)
		}

		document.addEventListener('AppleIDSignInOnSuccess', handleAuthSuccess)
		document.addEventListener('AppleIDSignInOnFailure', handleAuthFailure)

		return () => {
			document.removeEventListener(
				'AppleIDSignInOnSuccess',
				handleAuthSuccess,
			)
			document.removeEventListener(
				'AppleIDSignInOnFailure',
				handleAuthFailure,
			)
		}
	}, [onSubmit, onError, authType])

	return (
		<>
			<Script type="text/javascript" src={APPLE_AUTH_SCRIPT} />
			<button
				type="button"
				onClick={handleClick}
				className={cn(socialButtonStyle, className)}
			>
				<SolidApple className="shrink-0" />
				{variant === 'text' && (
					<span className={labelClassName}>
						{t('auth__signup__social_apple')}
					</span>
				)}
			</button>
		</>
	)
}
